// The MIT License (MIT)
//
// Copyright (c) 2016-2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import angular from 'angular';
import epfl_authgeoportailBase from '../epfl_authgeoportailmodule.js';
import epfl_authgeoportailCommon from './Common.js';
import epfl_authgeoportailFloorComponent from '../floor/component.js';
import epfl_authgeoportailSearchComponent from '../search/component.js';
import epfl_authgeoportailRoutingComponent from '../routing/component.js';
import epfl_authgeoportailIframeSearchService from '../iframeSearch/Service.js';
import epfl_authgeoportailIframeRoutingComponent from '../iframeRouting/component.js';
import gmfAbstractMobileController, {
  AbstractMobileController,
} from 'gmf/controllers/AbstractMobileController.js';

import './sass/iframe.scss';

/**
 * @private
 */
class Controller extends AbstractMobileController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {epfl_authgeoportail.iframeSearch.Service} iframeSearch Iframe search service.
   * @param {import("ngeo/map/BackgroundLayerMgr.js").MapBackgroundLayerManager} ngeoBackgroundLayerMgr Background layer manager.
   */
  constructor($scope, $injector, iframeSearch, ngeoBackgroundLayerMgr) {
    super($scope, $injector);

    // apply dimensions to any newly set background layer
    ngeoBackgroundLayerMgr.on(
      'change',
      () => {
        ngeoBackgroundLayerMgr.updateDimensions(this.map, this.dimensions);
      },
      this,
    );

    /**
     * @type {epfl_authgeoportail.Common}
     * @export
     */
    this.common = new epfl_authgeoportailCommon(this, $scope, $injector);
    const searchQuery = this.common.searchQuery;
    if (searchQuery) {
      iframeSearch.runQuery(this.map, searchQuery, this.dimensions, $scope);
    }
  }
}
Controller.$inject = ['$scope', '$injector', 'iframeSearchService', 'ngeoBackgroundLayerMgr'];

/**
 * @hidden
 */
const gmfModule = angular.module('Appiframe', [
  epfl_authgeoportailBase.name,
  epfl_authgeoportailCommon.module.name,
  gmfAbstractMobileController.name,
  epfl_authgeoportailFloorComponent.name,
  epfl_authgeoportailSearchComponent.name,
  epfl_authgeoportailRoutingComponent.name,
  epfl_authgeoportailIframeSearchService.name,
  epfl_authgeoportailIframeRoutingComponent.name,
]);

gmfModule.run([
  '$templateCache',
  /**
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    $templateCache.put('gmf/layertree', require('../layertree/layertree.html'));
    $templateCache.put('gmf/query/windowComponent', require('../displayquerywindow/displayquerywindow.html'));
  },
]);

gmfModule.constant('redirectUrl', '');

gmfModule.controller('IframeController', Controller);

export default gmfModule;
